import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import { useEffect, useRef } from 'react';
import { ellipsis } from '../../styles/utilities.css';
import { Flex } from '../Flex/Flex';
import { Icon } from '../Icon/Icon';
import { ScrollGradient } from '../ScrollGradient/ScrollGradient';
import { Text } from '../Text/Text';
import * as styles from './DrawerSelect.css';
import type { ItemProps, Key } from './Item';
import { Item } from './Item';
import { SELECT_ITEM_HEIGHT } from './Item.css';
import type { SelectItem } from './useSelect';

export const DRAWER_CLOSE_TIMEOUT: Milliseconds = 30;

export interface DrawerSelectItemProps {
  readonly item: DrawerSelectContentProps['itemsWithKey'][number];
  readonly selected: boolean;
  readonly onAction: (item: Key) => void;
}

export const DrawerSelectItem: React.FC<DrawerSelectItemProps> = ({ item, selected, onAction }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (selected) {
      timeout = setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: 'instant', block: 'center' });
      }, 0);
    }

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we want to run this effect only once
  }, []);

  return (
    <Item
      aria-label={item.label}
      disabled={item.disabled}
      drawer
      onAction={onAction}
      ref={ref}
      role="option"
      value={item.value}
    >
      <Flex
        alignItems="center"
        className={styles.drawerItem({ selected })}
        height={SELECT_ITEM_HEIGHT}
        justifyContent="space-between"
        padding={styles.DRAWER_ITEM_PADDING}
        width="100%"
      >
        <Text className={ellipsis} type="bodyMedium">
          {item.label}
        </Text>
        {selected && <Icon name="checkmark" size="sm" variant="primary" />}
      </Flex>
    </Item>
  );
};

type DrawerSelectContentProps = {
  readonly itemsWithKey: SelectItem[];
  readonly placeholder?: string;
  readonly onAction: (itemValue: Key) => void;
  readonly isSelectedItem: (item: ItemProps) => boolean;
};

export const DrawerSelectContent: React.FC<DrawerSelectContentProps> = ({
  itemsWithKey,
  placeholder,
  onAction,
  isSelectedItem,
}) => (
  <Flex alignItems="center" flexDirection="column" gap={styles.DRAWER_CONTENT_GAP} paddingInline={0}>
    <Text className={ellipsis} height={styles.DRAWER_TITLE_HEIGHT} textAlign="center" type="h5" width="100%">
      {placeholder}
    </Text>
    <ScrollGradient maxHeight={styles.DRAWER_CONTENT_HEIGHT}>
      <Flex aria-label={placeholder} className={styles.drawerContent} flexDirection="column" role="listbox">
        {itemsWithKey.map(item => (
          <DrawerSelectItem item={item} key={item.key} onAction={onAction} selected={isSelectedItem(item)} />
        ))}
      </Flex>
    </ScrollGradient>
  </Flex>
);
